/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { AppState } from 'src/app/store/app.reducer';
import { MtmaContact } from 'src/app/model/mtma-contact';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaContactUnit, MtmaUnit } from 'src/app/model/mtma-unit';
import { CustomValidators } from 'src/app/validators/custom.validators';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MtmaUnitContactDialogComponent } from '../mtma-manage-unit-contact-dialog/mtma-manage-unit-contact-dialog.component';
import { Subscription } from 'rxjs';
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-mtma-manage-unit-dialog',
	templateUrl: './mtma-manage-unit-dialog.component.html',
	providers: [DialogService]
})

export class MtmaUnitDialogComponent implements OnInit, OnDestroy {
	unitForm: FormGroup;

	mtmaUnitModifySub: Subscription;
	unit: MtmaUnit;

	adding = true;

	mtmaContactsSub: Subscription;

	ENTER = 13;
	COMMA = 188;

	separatorKeysCodes: number[] = [this.ENTER, this.COMMA];

	listContacts: MtmaContact[];
	listContactsSub: Subscription;

	contactsCtrl = new FormControl();
	filteredContacts: MtmaContact[];
	dateTransform: DatePipe;

	screenWidth: number;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private translate: TranslateService,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		public dialogService: DialogService,
	) {
		if (config.data !== undefined && config.data.unit !== undefined) {
			this.unit = config.data.unit;
			this.adding = false;
		} else {
			this.unit = new MtmaUnit();
		}
	}

	ngOnInit(): void {
		const curreLang = this.translate.currentLang;
		this.dateTransform = new DatePipe(curreLang);

		this.unitForm = new FormGroup({
			'Description': new FormControl(this.unit.Description, [Validators.maxLength(48)]),
			'Name': new FormControl(this.unit.Name, [Validators.required, Validators.maxLength(20)]),
			'KeyboardCode': new FormControl(this.unit.KeyboardCode, [Validators.maxLength(8), Validators.pattern('[0-9]+')]),
		});
		this.unitForm.addValidators(CustomValidators.checkNotLeadingZero('KeyboardCode'));
		this.unitForm.addValidators(CustomValidators.maxLengthOnByte('Name', 20));

		if (!this.unit.Contacts) {
			this.unit.Contacts = [];
		}
		if (!this.unit.ContactsInfo) {
			this.unit.ContactsInfo = [];
		}
		if ((!this.unit.ContactsInfo || this.unit.ContactsInfo.length === 0) && this.unit.Contacts && this.unit.Contacts.length > 0) {
			this.store.dispatch(DeviceActions.getMtmaUnit({ unit: this.unit }));
		}

		this.mtmaUnitModifySub = this.store.select('device', 'mtmaUnitModify')
			.pipe(filter(mtmaUnitModify => !!mtmaUnitModify))
			.subscribe(() => {
				this.resetClose();
			});

		this.store.dispatch(DeviceActions.getMtmaContactsAll());
		this.listContactsSub = this.store.select('device', 'mtmaContactsAll').subscribe(listContacts => {
			this.listContacts = listContacts;
		});

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	ngOnDestroy(): void {
		this.mtmaUnitModifySub?.unsubscribe();
		this.listContactsSub?.unsubscribe();
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.resetUnitForm());
		this.closeDialog();
	}

	filterContacts(event) {
		const filtered: any[] = [];
		const query = event.query;
		if (this.listContacts) {
			for (let i = 0; i < this.listContacts.length; i++) {
				const contact = this.listContacts[i];
				if (contact.Name.toLowerCase().indexOf(query.toLowerCase()) >= 0) {
					filtered.push(contact);
				}
			}
		}
		this.filteredContacts = filtered;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.unitForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.unitForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	save(): void {
		this.unit = {
			...this.unit,
			Description: this.f.Description.value,
			Name: this.f.Name.value,
			KeyboardCode: this.f.KeyboardCode.value,
		};
		this.unit.Contacts = Array<MtmaContactUnit>();
		for (const contact of this.unit.ContactsInfo) {
			const cont = new MtmaContactUnit();
			cont.ContactId = contact.ContactId;
			cont.RuleId = contact.RuleId;
			cont.DtFrom = contact.DtFrom;
			cont.DtTo = contact.DtTo;
			this.unit.Contacts.push(cont);
		}

		if (this.unit.ObjId) {
			this.store.dispatch(DeviceActions.editMtmaUnit({ unit: this.unit }));
		} else {
			this.store.dispatch(DeviceActions.addMtmaUnit({ unit: this.unit }));
		}
	}

	deleteContact(position: number): void {
		if (position >= 0) {
			this.unit.ContactsInfo.splice(position, 1);
		}
	}

	filter(value: string): MtmaContact[] {
		if (typeof value === 'string') {
			const filterValue = value.toLowerCase();
			return this.listContacts.filter(contact => contact.Name.toLowerCase().indexOf(filterValue) >= 0 || contact.Description.toLowerCase().indexOf(filterValue) >= 0);
		}
		return this.listContacts;
	}

	getOptionText(option) {
		if (option === null) {
			return;
		}
		return option.Name;
	}

	disabledAddContactUnit(): boolean {
		return this.unit.ContactsInfo.length >= 5;
	}

	checkContactsCtrl(): boolean {
		return !(this.contactsCtrl.value !== null && this.contactsCtrl.value !== undefined && this.contactsCtrl.value !== "") || (this.unit.ContactsInfo.length > 4);
	}

	addContactUnit() {
		if (this.contactsCtrl.value !== null || this.contactsCtrl.value as MtmaContact) {
			const contact = new MtmaContactUnit();
			contact.ContactId = this.contactsCtrl.value.ObjId;
			contact.Contact = this.contactsCtrl.value;
			this.unit.ContactsInfo.push(contact);
		}
	}

	editContact(contact: MtmaContactUnit) {
		this.dialogService.open(MtmaUnitContactDialogComponent, {
			header: this.translate.instant('EDIT_TIMESLICE_CONTACT'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				contact
			}
		});
	}

	getRuleData(element: MtmaContactUnit): string {
		let txt = '';
		if (element.RuleId !== undefined) {
			txt = ' - ' + element.RuleName;
			if (element.DtFrom !== undefined && element.DtTo !== undefined) {
				txt = txt + ' - ' + this.translate.instant('FROM') + ' ' + this.dateTransform.transform(element.DtFrom, 'dd/MM/yyyy HH:mm') +
					' - ' + this.translate.instant('TO') + ' ' + this.dateTransform.transform(element.DtTo, 'dd/MM/yyyy HH:mm');
			}
		}
		return txt;
	}

	get f(): any {
		return this.unitForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}


	orderUp(contactIndex: number, contact: MtmaContactUnit) {
		const newIndex = contactIndex - 1; // La nuova posizione (una in alto)

		// 2. Rimuovi l'elemento dalla sua posizione attuale
		this.unit.ContactsInfo.splice(contactIndex, 1);

		// 3. Inserisci l'elemento nella nuova posizione
		this.unit.ContactsInfo.splice(newIndex, 0, contact);
	}

	orderDown(contactIndex: number, contact: MtmaContactUnit) {
		const newIndex = contactIndex + 1; // La nuova posizione (una in alto)

		// 2. Rimuovi l'elemento dalla sua posizione attuale
		this.unit.ContactsInfo.splice(contactIndex, 1);

		// 3. Inserisci l'elemento nella nuova posizione
		this.unit.ContactsInfo.splice(newIndex, 0, contact);
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-23rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: MtmaUnitDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: MtmaUnitDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	dynamicAutocomplete(): string {
		return StandardDynamicComponent.dynamicAutocomplete(this.screenWidth);
	}

}